<template>
  <div>
    <div class="details-component">
      <div class="details-component__header">
        <h1>{{ $t('common.parking_permit') }}</h1>
        <div class="details-component__header_right" v-if="!isDeleted() && (isLandlord() || isTenant())">
          <router-link :to="{name: 'edit-permit', params: { id: selectedPermitId }}" class="link" >
          {{ $t('common.change') }}&nbsp;<i class="icon icon--arrow"></i>
          </router-link>
          <a v-on:click="showDeletingConfirmation" class="link link--orange">{{ $t('common.delete') }}&nbsp;<i class="icon icon--cross"></i></a>
        </div>
      </div>
      <div class="details-component__content content" v-if="Object.keys(permit).length !== 0">
          <table class="details-table no-col-padding fixed-table label-col-wide-260" v-bind:class="{ content__cell: permit.parkingCardNr }">
            <tr class="details-table__row" v-if="isSuperAdmin() && permit.partner">
              <td class="details-table__col details-table__col_label">{{ $t('common.landlord') }}</td>
              <td class="details-table__col landlord__name" v-if="permit.partner.landlord">{{ permit.partner.landlord.name }}</td>
            </tr>
            <tr class="details-table__row" v-if="permit.parkingCardNr">
              <td class="details-table__col details-table__col_label">{{ $t('common.card_number') }}</td>
              <td class="details-table__col details-table__col_signifier bold">{{ permit.parkingCardNr }}</td>
            </tr>
            <tr class="details-table__row" v-if="permit.vehicle">
              <td class="details-table__col details-table__col_label">{{ $t('permits.plate_nr') }}</td>
              <td class="details-table__col">{{ permit.vehicle.vehicleReg }}</td>
            </tr>
            <tr class="details-table__row" v-if="permit.product">
              <td class="details-table__col details-table__col_label">{{ $t('common.product') }}</td>
              <td class="details-table__col">
                <router-link :to="{ name: 'product', params: { id: permit.product.id }, query: { keyword: permit.product.name } }" class="link link--blue">
                    {{ permit.product.name }}
                </router-link>
              </td>
            </tr>
            <tr class="details-table__row" v-if="permit.user">
              <td class="details-table__col details-table__col_label">{{ $t('common.user') }}</td>
              <td class="details-table__col">
                <router-link :to="{ name: 'user', params: { id: permit.user.id }, query: { keyword: permit.user.name } }" class="link link--blue">
                    {{ permit.user.name }}
                </router-link>
              </td>
            </tr>
            <tr class="details-table__row" v-if="isLandlord() && permit.partner">
              <td class="details-table__col details-table__col_label">{{ $t('common.issued_by') }}</td>
              <td class="details-table__col">
                <router-link :to="{ name: 'tenant', params: { id: permit.partner.id }, query: { keyword: permit.partner.name } }" class="link link--blue">
                    {{ permit.partner.name }}
                </router-link>
              </td>
            </tr>
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('permits.valid_from_date') }}</td>
              <td class="details-table__col">{{ permit.validFromDate }}</td>
            </tr>
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('permits.expiration_date') }}</td>
              <td class="details-table__col">{{ permit.expirationDate }}</td>
            </tr>
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('common.created_at') }}</td>
              <td class="details-table__col">{{ permit.created_at }}</td>
            </tr>
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('permits.parking_spot_number') }}</td>
              <td class="details-table__col">{{ permit.parkingSpotNumber }}</td>
            </tr>
          </table>
          <div class="content__cell content__cell_centered-stack" v-if="permit.parkingCardNr">
            <div class="permit-card">
              <div class="permit-card__number">{{ permit.parkingCardNr }}</div>
            </div>
          </div>
        </div>
    </div>
    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelPermitDelete" @confirmed="deletePermit">
      <h1 slot="header">{{ $t('common.deleting') }}: {{ permit.vehicle_owner_name }} </h1>
      <p slot="body">{{ $t('permits.confirm_delete') }}</p>
    </ConfirmationModal>
    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ permitWarningHeader }}</h1>
      <p slot="body">{{ permitWarningBody }}</p>
    </WarningModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';

export default {
  components: {
    ConfirmationModal,
    WarningModal,
  },
  computed: mapGetters({
    permit: 'permits/permit/permit',
    permitError: 'permits/permit/error',
    selectedPermitId: 'permits/permit/selectedPermitId',
  }),
  watch: {
    $route: 'fetchPermit',
  },
  route: {
    canReuse: true,
  },
  created() {
    if (this.$route.params.id) {
      this.fetchPermit();
    }
  },
  methods: {
    isDeleted() {
      return this.permit.isDeleted;
    },
    fetchPermit() {
      if (this.$route.params.id && this.user.data) {
        this.$store.dispatch('permits/permit/fetch', { id: this.$route.params.id, user: this.user });
      }
    },
    showDeletingConfirmation() {
      this.showConfirmationModal = true;
    },
    deletePermit() {
      this.showConfirmationModal = false;
      this.$store.dispatch('permits/permit/delete', { id: this.selectedPermitId, user: this.user }).then(() => {
        if (this.permitError) {
          this.permitWarningHeader = this.$t('common.deleting');
          this.permitWarningBody = this.$t('permits.delete_error');
          this.showWarningModal = true;
        } else {
          this.$store.commit(`landlord/permits/${mTypes.REMOVE_DATA}`, this.selectedPermitId);
          this.$router.push({ name: 'permits' });
        }
      });
    },
    cancelPermitDelete() {
      this.showConfirmationModal = false;
    },
    closeWarningModal() {
      this.showWarningModal = false;
    },
  },
  data() {
    return {
      showConfirmationModal: false,
      showWarningModal: false,
      permitWarningBody: null,
      permitWarningHeader: null,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/blocks/permit.scss';
@import '~styles/blocks/details.scss';
@import '~node_modules/leaflet/dist/leaflet.css';

.details-component {

  &__content {

    @media screen and (max-width: 1350px) {
      flex-direction: column;

      .details-table {
        width: 100%;
      }

    }

  }

}

</style>



// WEBPACK FOOTER //
// src/components/permits/PermitDetails.vue